// ─── Copy Trade Auto-Restore ────────────────────────────────
// Restarts the copy trade engine after a page reload if it was
// running before. Prefers Supabase (cross-device), falls back to
// localStorage (same-browser). Mirrors useTrailingGlobalRestore and
// useChinGlobalRestore — called once from Layout after auth resolves.

import { useEffect, useRef } from 'react';
import {
  startCopyTradeEngine,
  stopCopyTradeEngine,
  isCopyTradeEngineRunning,
  wasEngineActiveBeforeReload,
  resolveCopyTradePairs,
} from '@/lib/copy-trade-engine';

export function useCopyTradeAutoRestore(userId: string | null) {
  const restoredRef = useRef(false);

  useEffect(() => {
    // Logout: stop engine and reset the restore guard
    if (!userId) {
      if (isCopyTradeEngineRunning()) stopCopyTradeEngine();
      restoredRef.current = false;
      return;
    }

    // Already restored this session, or engine already running → skip
    if (restoredRef.current || isCopyTradeEngineRunning()) return;

    (async () => {
      // Pick the FRESHEST config (Supabase cross-device vs this-browser
      // localStorage). This prevents restarting the engine with a stale volume
      // setting whose newer edit is still in flight to Supabase.
      const resolved = await resolveCopyTradePairs();
      const configs = resolved.pairs
        .filter((p) => p.sourceAccountId && p.targetMt4AccountId)
        .map((p) => ({
          sourceAccountId: p.sourceAccountId,
          targetMt4AccountId: p.targetMt4AccountId,
          strategyId: p.strategyId,
          enabled: p.enabled !== false,
          volumeMode: p.volumeMode || 'same',
          volumePercent: p.volumePercent,
          fixedVolume: p.fixedVolume,
        }));

      if (configs.length > 0 && (resolved.wasRunning || wasEngineActiveBeforeReload())) {
        restoredRef.current = true;
        startCopyTradeEngine(configs);
        console.log(`[CopyTradeAutoRestore] ✓ Restored ${configs.length} pair(s) (fresh source)`);
        return;
      }

      // Nothing persisted / engine wasn't running → leave it stopped.
      console.log('[CopyTradeAutoRestore] No restorable running config found.');
    })();
  }, [userId]);
}